/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react-hooks/exhaustive-deps */

import { Table } from '@tanstack/react-table';
import { Mail, User, Filter, Building2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { DataTableViewOptions } from 'components/blocks/data-table/data-table-view-options';
import { useEffect, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from 'components/ui/sheet';
import { DateRange } from 'react-day-picker';
import FilterControls from './filter-controls';
import { useTranslation } from 'react-i18next';

/**
 * Renders a toolbar for the IAM (Identity and Access Management) table, allowing users to search
 * and filter data based on email, name, and date ranges, as well as reset filters.
 *
 * Features:
 * - Provides an input field for searching by email or name
 * - Allows toggling between search modes (email vs name)
 * - Includes mobile-responsive filters using a sheet and desktop filters
 * - Supports date range filters for creation date and last login date
 * - Displays a count of active filters and allows resetting them
 *
 * @param {IamTableToolbarProps<TData>} props - The props for configuring the toolbar
 * @param {Table<TData>} props.table - The table instance for controlling the data
 * @param {function} [props.onSearch] - Optional callback function triggered when filters are applied
 * @param {Array} props.columns - The columns used for rendering the table
 *
 * @returns {JSX.Element} - The rendered toolbar with search, filter, and reset options
 *
 * @example
 * <IamTableToolbar
 *   table={tableInstance}
 *   onSearch={(filters) => console.log('Filters applied:', filters)}
 *   columns={columns}
 * />
 */

interface IamTableToolbarProps<TData> {
  table: Table<TData>;
  onSearch?: (filters: { email: string; name: string; organizationIds: string[] }) => void;
  columns: any[];
}

export function IamTableToolbar<TData>({ table, onSearch }: Readonly<IamTableToolbarProps<TData>>) {
  const [filters, setFilters] = useState({
    email: '',
    name: '',
    organizationIds: [] as string[],
  });
  const [searchMode, setSearchMode] = useState<'email' | 'name'>('name');
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [dateRangeCreate, setDateRangeCreate] = useState<DateRange | undefined>(undefined);
  const [dateRangeLastLogin, setDateRangeLastLogin] = useState<DateRange | undefined>(undefined);
  const { t } = useTranslation();
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);

  const getFilterColumn = (columnId: string) => {
    return table.getAllFlatColumns().find((col) => col.id === columnId);
  };

  const debouncedSearch = useCallback(
    debounce((newFilters) => {
      onSearch?.(newFilters);
    }, 500),
    [onSearch]
  );

  useEffect(() => {
    debouncedSearch(filters);
    return () => {
      debouncedSearch.cancel();
    };
  }, [filters, debouncedSearch]);

  useEffect(() => {
    const newFilters = { ...filters };

    newFilters.organizationIds = organizationIds;
    setFilters(newFilters);
    onSearch?.(newFilters);
  }, [organizationIds]);

  const handleFilterChange = (value: string) => {
    const newFilters = { ...filters, [searchMode]: value };
    setFilters(newFilters);

    if (searchMode === 'email') {
      getFilterColumn('email')?.setFilterValue(value);
    } else if (searchMode === 'name') {
      getFilterColumn('fullName')?.setFilterValue(value);
    }
  };

  const toggleSearchMode = () => {
    const currentMode = searchMode;
    const nextMode = currentMode === 'email' ? 'name' : 'email';
    setSearchMode(nextMode);

    const currentValue = filters[searchMode];
    const newFilters = {
      ...filters,
      [searchMode]: '',
      [nextMode]: currentValue,
    };
    setFilters(newFilters);

    const emailColumn = getFilterColumn('email');
    const fullNameColumn = getFilterColumn('fullName');
    // const organizationColumn = getFilterColumn('organization');

    // Clear all columns first
    emailColumn?.setFilterValue('');
    fullNameColumn?.setFilterValue('');
    // organizationColumn?.setFilterValue('');

    // Set the new mode's column value
    if (nextMode === 'email') {
      emailColumn?.setFilterValue(currentValue);
    } else if (nextMode === 'name') {
      fullNameColumn?.setFilterValue(currentValue);
    }

    // else {
    //   organizationColumn?.setFilterValue(currentValue);
    // }

    onSearch?.(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({ email: '', name: '', organizationIds: [] });
    setDateRangeCreate(undefined);
    setDateRangeLastLogin(undefined);
    table.resetColumnFilters();
    onSearch?.({ email: '', name: '', organizationIds: [] });
    setOrganizationIds([]);
  };

  const isFiltered = filters.email || filters.name || filters.organizationIds.length > 0 || table.getState().columnFilters.length > 0;

  const activeFiltersCount =
    table.getState().columnFilters.length + (filters.email || filters.name || filters.organizationIds.length > 0 ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-0 sm:flex sm:items-start sm:justify-between h-auto">
      <div className="flex flex-col w-full gap-4 sm:flex-row sm:items-start">
        <div className="flex items-start gap-2">
          <div className="flex flex-col xl:flex-row gap-2 flex-1 sm:w-[250px] xl:w-[300px] min-w-[140px] border border-primary rounded-3xl overflow-hidden h-[40px]">
            <div className="relative w-full items-center flex">
              <Input
                placeholder={`${t('SEARCH_BY')} ${searchMode === 'email' ? t('EMAIL').toLowerCase() : searchMode === 'name' ? t('NAME').toLowerCase() : t('ORGANIZATION').toLowerCase()}...`}
                value={filters[searchMode]}
                onChange={(event) => handleFilterChange(event.target.value)}
                className="h-8 w-full rounded-lg bg-background pr-20 border-none focus:ring-0 focus:ring-offset-0 h-10"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSearchMode}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
              >
                {searchMode === 'email' ? <Mail className="h-4 w-4" /> : searchMode === 'name' ? <User className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Mobile Filter Button */}
          <div className="sm:hidden">
            <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-3 whitespace-nowrap">
                  <Filter className="h-2 w-2" />
                  {activeFiltersCount > 0 && (
                    <span className="ml-1 rounded-full bg-primary w-5 h-5 text-xs flex items-center justify-center text-primary-foreground">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="w-full" aria-describedby="filter-description">
                <SheetHeader>
                  <SheetTitle>{t('FILTERS')}</SheetTitle>
                </SheetHeader>
                <div className="py-4">
                  <FilterControls
                    table={table}
                    dateRangeCreate={dateRangeCreate}
                    dateRangeLastLogin={dateRangeLastLogin}
                    onDateRangeCreateChange={setDateRangeCreate || (() => { })}
                    onDateRangeLastLoginChange={setDateRangeLastLogin || (() => { })}
                    organizationIds={organizationIds}
                    setOrganizationIds={setOrganizationIds}
                    handleResetFilters={handleResetFilters}
                    isFiltered={isFiltered}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="flex flex-row gap-1 flex-wrap">
          {/* Desktop Filters */}
          <div className="hidden sm:block">
            <FilterControls
              table={table}
              dateRangeCreate={dateRangeCreate}
              dateRangeLastLogin={dateRangeLastLogin}
              onDateRangeCreateChange={setDateRangeCreate || (() => { })}
              onDateRangeLastLoginChange={setDateRangeLastLogin || (() => { })}
              organizationIds={organizationIds}
              setOrganizationIds={setOrganizationIds}
              handleResetFilters={handleResetFilters}
              isFiltered={isFiltered}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end w-full sm:w-auto">
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}

export default IamTableToolbar;
